import { useEffect, useState } from 'react'
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuthModal } from '@/context/AuthModalContext'
import { useLoader } from '@/context/LoaderContext'
import { useDefaultCurrencyCode } from '@/hooks/use-default-currency'
import { showApiErrorToast } from '@/lib/api-toast'
import { formatBalance } from '@/lib/format-number'
import { calculateP2pBuyMetrics, formatP2pPrice, getP2pBuyPrice, getP2pOptionLogo, getSelectedP2pOption } from '@/lib/p2p'
import { isValidP2pBuyAmount, parseP2pBuyAmount } from '@/lib/p2p-validation'
import { cn } from '@/lib/utils'
import { useBuyP2pMarketMutation } from '@/store/api/p2pApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { selectP2pBuyAmount, selectP2pOptionType, selectSelectedP2pOptionId, setP2pBuyAmount, setP2pOptionType } from '@/store/slices/p2pSlice'
import type { P2PMarket, P2pBuyRequest } from '@/types/p2p'

interface P2pBuyPanelProps {
	market: P2PMarket
}

export function P2pBuyPanel({ market }: P2pBuyPanelProps) {
	const dispatch = useAppDispatch()
	const selectedOptionId = useAppSelector(selectSelectedP2pOptionId)
	const selectedOption = getSelectedP2pOption(market, selectedOptionId)
	const buySide = useAppSelector(selectP2pOptionType)
	const buyAmount = useAppSelector(selectP2pBuyAmount)
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const currencyCode = useDefaultCurrencyCode()
	const { openLogin } = useAuthModal()
	const [buyP2pMarket, { isLoading: isJoining }] = useBuyP2pMarketMutation()
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)

	const { showLoader, hideLoader } = useLoader()

	useEffect(() => {
		if (!isJoining) {
			return
		}

		showLoader()
		return () => hideLoader()
	}, [isJoining, showLoader, hideLoader])

	if (!selectedOption) {
		return null
	}

	const yesPrice = getP2pBuyPrice(selectedOption, 'yes')
	const noPrice = getP2pBuyPrice(selectedOption, 'no')
	const numericAmount = Number.parseFloat(buyAmount) || 0
	const averagePrice = getP2pBuyPrice(selectedOption, buySide)
	const { shares, potentialReturn } = calculateP2pBuyMetrics(numericAmount, averagePrice)
	const returnPercent = numericAmount > 0 ? ((potentialReturn / numericAmount) * 100).toFixed(0) : '0'
	const canSubmit = isValidP2pBuyAmount(buyAmount, currencyCode) && !isJoining
	const sideLabel = buySide === 'yes' ? 'Yes' : 'No'

	const handleAmountChange = (value: string) => {
		if (value === '' || /^\d*\.?\d*$/.test(value)) {
			dispatch(setP2pBuyAmount(value))
		}
	}

	const handleQuickAmount = (multiplier: number) => {
		const nextAmount = Math.max(0, numericAmount * multiplier)
		dispatch(setP2pBuyAmount(String(nextAmount)))
	}

	const handleBuyClick = () => {
		if (!canSubmit) {
			return
		}

		if (!currencyCode?.trim()) {
			showApiErrorToast('Currency is required')
			return
		}

		if (parseP2pBuyAmount(buyAmount) === null) {
			showApiErrorToast('Invalid amount')
			return
		}

		setIsConfirmOpen(true)
	}

	const handleConfirmBuy = async () => {
		if (isJoining) {
			return
		}

		if (!currencyCode?.trim()) {
			showApiErrorToast('Currency is required')
			return
		}

		const amount = parseP2pBuyAmount(buyAmount)
		if (amount === null) {
			showApiErrorToast('Invalid amount')
			return
		}

		const payload: P2pBuyRequest = {
			market_id: market.id,
			option_id: selectedOption.id,
			currency_code: currencyCode.trim(),
			amount,
			option_type: buySide,
		}

		try {
			await buyP2pMarket(payload).unwrap()
			setIsConfirmOpen(false)
		} catch {
			// Error toast is handled by playerBaseQuery.
		}
	}

	return (
		<div className='w-full rounded-2xl border border-border bg-card p-4 shadow-[0_4px_12px_0_hsl(var(--background)/0.2)] sm:p-5'>
			<div className='mb-5 flex items-center gap-3'>
				<img src={getP2pOptionLogo(selectedOption, market)} alt='' className='h-10 w-10 shrink-0 rounded-full object-cover' />
				<p className='truncate text-lg font-semibold tracking-[-0.27px] text-foreground'>{selectedOption.name}</p>
			</div>

			<div className='mb-5 grid grid-cols-2 gap-2'>
				<button
					type='button'
					onClick={() => dispatch(setP2pOptionType('yes'))}
					className={cn(
						'relative overflow-hidden rounded-xl border px-4 py-3 text-sm font-semibold tracking-[-0.21px] transition-colors',
						buySide === 'yes' ? 'border-info bg-info text-white ring-1 ring-info/60' : 'border-info bg-background text-info hover:bg-info/10',
					)}
				>
					{buySide !== 'yes' ? (
						<span className='pointer-events-none absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-info opacity-60 blur-xl' aria-hidden />
					) : null}
					<span className='relative'>Yes ${formatP2pPrice(String(yesPrice))}</span>
				</button>
				<button
					type='button'
					onClick={() => dispatch(setP2pOptionType('no'))}
					className={cn(
						'relative overflow-hidden rounded-xl border px-4 py-3 text-sm font-semibold tracking-[-0.21px] transition-colors',
						buySide === 'no' ? 'border-warning bg-warning text-white ring-1 ring-warning/60' : 'border-warning bg-background text-warning hover:bg-warning/10',
					)}
				>
					{buySide !== 'no' ? (
						<span className='pointer-events-none absolute left-2 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-warning opacity-60 blur-xl' aria-hidden />
					) : null}
					<span className='relative'>No ${formatP2pPrice(String(noPrice))}</span>
				</button>
			</div>

			<div className='space-y-4'>
				<div>
					<label htmlFor='p2p-buy-amount' className='mb-2 block text-sm font-medium tracking-[-0.21px] text-muted-foreground'>
						Amount
					</label>
					<div className='flex items-center gap-2'>
						<Input
							id='p2p-buy-amount'
							inputMode='decimal'
							value={buyAmount}
							onChange={(event) => handleAmountChange(event.target.value)}
							disabled={isJoining}
							className='h-11 flex-1 rounded-xl border-border bg-background text-base'
							placeholder='0.00'
						/>
						<button
							type='button'
							onClick={() => handleQuickAmount(0.5)}
							disabled={isJoining}
							className='h-11 shrink-0 rounded-xl border border-border bg-secondary px-3 text-sm font-medium tracking-[-0.21px] text-foreground transition-colors hover:bg-accent disabled:opacity-50'
						>
							1/2
						</button>
						<button
							type='button'
							onClick={() => handleQuickAmount(2)}
							disabled={isJoining}
							className='h-11 shrink-0 rounded-xl border border-border bg-secondary px-3 text-sm font-medium tracking-[-0.21px] text-foreground transition-colors hover:bg-accent disabled:opacity-50'
						>
							2X
						</button>
					</div>
				</div>

				<div className='space-y-2.5'>
					<div className='flex items-center justify-between text-sm tracking-[-0.21px]'>
						<span className='text-muted-foreground'>Avg price</span>
						<span className='font-medium text-foreground'>${formatBalance(averagePrice, 2)}</span>
					</div>
					<div className='flex items-center justify-between text-sm tracking-[-0.21px]'>
						<span className='text-muted-foreground'>Shares</span>
						<span className='font-medium text-foreground'>{formatBalance(shares, 2)}</span>
					</div>
					<div className='flex items-center justify-between text-sm tracking-[-0.21px]'>
						<span className='text-muted-foreground'>Potential return</span>
						<span className='font-medium text-primary'>
							${formatBalance(potentialReturn, 2)} ({returnPercent}%)
						</span>
					</div>
				</div>

				{isAuthenticated ? (
					<button
						type='button'
						onClick={handleBuyClick}
						disabled={!canSubmit}
						className={cn(
							'w-full rounded-xl px-4 py-3.5 text-base font-semibold tracking-[-0.24px] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
							buySide === 'yes' ? 'bg-info text-white' : 'bg-warning text-white',
						)}
					>
						Buy {sideLabel}
					</button>
				) : (
					<button
						type='button'
						onClick={openLogin}
						className='w-full rounded-xl bg-primary px-4 py-3.5 text-base font-semibold tracking-[-0.24px] text-primary-foreground transition-opacity hover:opacity-90'
					>
						Sign In
					</button>
				)}
			</div>

			<AlertDialog
				open={isConfirmOpen}
				onOpenChange={(open) => {
					if (isJoining) {
						return
					}

					setIsConfirmOpen(open)
				}}
			>
				<AlertDialogContent className='max-w-md gap-5 rounded-2xl border-border bg-card p-5 sm:rounded-2xl sm:p-6'>
					<AlertDialogHeader className='space-y-3 text-left sm:text-left'>
						<AlertDialogTitle className='text-xl font-semibold tracking-[-0.27px] text-foreground'>Confirm purchase</AlertDialogTitle>
						<AlertDialogDescription className='sr-only'>
							Confirm buying {sideLabel} on {selectedOption.name} for {formatBalance(numericAmount)} {currencyCode}.
						</AlertDialogDescription>

						<div className='flex items-center gap-3 rounded-xl border border-border bg-secondary/60 p-3'>
							<img src={getP2pOptionLogo(selectedOption, market)} alt='' className='h-11 w-11 shrink-0 rounded-full object-cover' />
							<div className='min-w-0 flex-1'>
								<p className='truncate text-base font-semibold tracking-[-0.24px] text-foreground'>{selectedOption.name}</p>
								<p className='mt-0.5 text-sm tracking-[-0.21px] text-muted-foreground'>{market.title}</p>
							</div>
							<span
								className={cn(
									'shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold tracking-[-0.18px]',
									buySide === 'yes' ? 'bg-info/15 text-info' : 'bg-warning/15 text-warning',
								)}
							>
								{sideLabel}
							</span>
						</div>
					</AlertDialogHeader>

					<div className='space-y-2.5 rounded-xl border border-border bg-background/60 px-4 py-3'>
						<div className='flex items-center justify-between gap-3 text-sm tracking-[-0.21px]'>
							<span className='text-muted-foreground'>Amount</span>
							<span className='font-medium tabular-nums text-foreground'>
								{formatBalance(numericAmount)} {currencyCode}
							</span>
						</div>
						<div className='flex items-center justify-between gap-3 text-sm tracking-[-0.21px]'>
							<span className='text-muted-foreground'>Avg price</span>
							<span className='font-medium tabular-nums text-foreground'>${formatBalance(averagePrice, 2)}</span>
						</div>
						<div className='flex items-center justify-between gap-3 text-sm tracking-[-0.21px]'>
							<span className='text-muted-foreground'>Shares</span>
							<span className='font-medium tabular-nums text-foreground'>{formatBalance(shares, 2)}</span>
						</div>
						<div className='flex items-center justify-between gap-3 border-t border-border pt-2.5 text-sm tracking-[-0.21px]'>
							<span className='text-muted-foreground'>Potential return</span>
							<span className='font-semibold tabular-nums text-primary'>
								${formatBalance(potentialReturn, 2)} ({returnPercent}%)
							</span>
						</div>
					</div>

					<AlertDialogFooter className='gap-2 sm:space-x-0'>
						<Button type='button' variant='secondary' className='w-full sm:w-auto sm:min-w-[8.5rem]' disabled={isJoining} onClick={() => setIsConfirmOpen(false)}>
							Cancel
						</Button>
						<Button type='button' variant='primary' className='w-full sm:w-auto sm:min-w-[8.5rem]' disabled={isJoining} onClick={() => void handleConfirmBuy()}>
							{`Buy ${sideLabel}`}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
